import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import { useEffect, useCallback, useRef } from 'react';
import { AddonSelector } from '../../AddonSelector';
import { AddonPricingSummary } from '../../AddonPricingSummary';
import { useAvailableAddons } from '../../../hooks/useAvailableAddons';
import type { AddonPricingSummary as AddonPricingItem } from '@lov/shared';

interface AddonsSectionProps {
  venueInventoryId: string;
  date: string;
  startTime: string;
  endTime: string;
  selectedAddons: Map<string, number>;
  onAddonsChange: (addons: Map<string, number>) => void;
  onComplete: (data: unknown, summary: string) => void;
  registerContinue?: (callback: () => void) => void;
}

export function AddonsSection({
  venueInventoryId,
  date,
  startTime,
  endTime,
  selectedAddons,
  onAddonsChange,
  onComplete,
  registerContinue,
}: AddonsSectionProps) {
  const { addons, durationHours, isLoading } = useAvailableAddons(
    venueInventoryId,
    date,
    startTime,
    endTime,
    { enablePolling: true }
  );

  const hasAddons = addons.length > 0;
  const hasSelections = selectedAddons.size > 0;

  // Build pricing summary for selected addons
  const pricingSummary: AddonPricingItem[] = Array.from(selectedAddons.entries())
    .map(([addonId, quantity]) => {
      const addon = addons.find((a) => a.id === addonId);
      if (!addon) return null;

      // Calculate duration multiplier based on pricing model
      let durationMultiplier = 1; // FIXED
      if (addon.pricingModel === 'HOURLY') {
        durationMultiplier = durationHours;
      } else if (addon.pricingModel === 'HALF_HOURLY') {
        durationMultiplier = durationHours; // Will be multiplied by 2 in rate display
      }

      const subtotal = addon.pricePerUnit * quantity * durationMultiplier;

      return {
        addonItemId: addon.id,
        addonName: addon.name,
        quantity,
        pricePerUnit: addon.pricePerUnit,
        pricingModel: addon.pricingModel,
        durationMultiplier,
        subtotal,
      } as AddonPricingItem;
    })
    .filter((item): item is AddonPricingItem => item !== null);

  const handleContinue = useCallback(() => {
    // Convert Map to array for session storage.
    // Map key is addon.id = addonVenueInventory.id (the addon's own VenueInventory UUID).
    // We look up addon.addonItemId for the catalog UUID required by the backend.
    const addonArray = Array.from(selectedAddons.entries()).map(
      ([addonVenueInventoryId, quantity]) => {
        const addon = addons.find((a) => a.id === addonVenueInventoryId);
        return {
          addonItemId: addon?.addonItemId ?? addonVenueInventoryId, // catalog UUID fallback
          venueInventoryId: addonVenueInventoryId, // addon's own VenueInventory.id
          quantity,
        };
      }
    );

    const summary = hasSelections
      ? `${selectedAddons.size} add-on${selectedAddons.size > 1 ? 's' : ''} selected`
      : 'No add-ons';

    onComplete({ selectedAddons: addonArray }, summary);
  }, [selectedAddons, addons, hasSelections, onComplete]);

  useEffect(() => {
    if (registerContinue) {
      registerContinue(handleContinue);
    }
  }, [registerContinue, handleContinue]);

  // Auto-complete when no addons are available (skip the section silently)
  const hasAutoCompleted = useRef(false);
  useEffect(() => {
    if (!isLoading && !hasAddons && !hasAutoCompleted.current) {
      hasAutoCompleted.current = true;
      onComplete({ selectedAddons: [] }, 'No add-ons');
    }
  }, [isLoading, hasAddons, onComplete]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If no addons, render nothing (auto-complete useEffect will fire)
  if (!hasAddons) {
    return null;
  }

  return (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Optional - enhance your booking with additional equipment
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              onAddonsChange(new Map());
              onComplete({ selectedAddons: [] }, 'No add-ons');
            }}
          >
            Skip add-ons →
          </Button>
        </Box>

        <AddonSelector
          venueInventoryId={venueInventoryId}
          date={date}
          startTime={startTime}
          endTime={endTime}
          selectedAddons={selectedAddons}
          onAddonsChange={onAddonsChange}
          enablePolling={true}
        />

        {hasSelections && (
          <Box sx={{ mt: 2 }}>
            <AddonPricingSummary
              selectedAddons={pricingSummary}
              durationHours={durationHours}
            />
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleContinue} size="large">
            {hasSelections ? 'Continue' : 'Skip Add-ons'}
          </Button>
        </Box>
      </Box>
    </motion.div>
  );
}
